import { Badge, Button, EmptyState, Input } from '@personachat/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ClipboardList, Download, Filter } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components, paths } from '../../api/schema';
import { useSession } from '../../auth/AuthProvider';
import { ErreurChargement, Filtre, Select } from '../../components/bits';
import { DataTable, type Colonne } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { enregistrer, nomDate } from '../../lib/fichiers';
import { bornesJourParis, formatDateTime, jourParis } from '../../lib/format';
import { useFiltresUrl } from '../../lib/urlState';
import { PageHeader } from '../../pages/PageHeader';

import { differences, libelleAction } from './AuditTimeline';

type Entree = components['schemas']['AuditEntry'];
type TypeObjet = components['schemas']['AuditObjectType'];
type RequeteJournal = NonNullable<paths['/api/admin/audit']['get']['parameters']['query']>;

export const LIBELLE_OBJET: Record<TypeObjet, string> = {
  student: 'Élève',
  credits: 'Crédits',
  accessKey: 'Clé d’accès',
  unlockKey: 'Clé de déblocage',
  pack: 'Pack',
  plan: 'Abonnement',
  event: 'Événement',
  module: 'Module',
  coach: 'Coach',
  admin: 'Administrateur',
  settings: 'Réglages',
};

/** Page de l'objet visé, quand elle existe (la fiche des admins est réservée au super admin). */
export function lienObjet(type: TypeObjet | null | undefined, id: string | null | undefined, superAdmin: boolean): string | null {
  if (!type || !id) return null;
  switch (type) {
    case 'student':
    case 'credits':
      return `/eleves/${id}`;
    case 'accessKey':
      return `/cles-acces/${id}`;
    case 'unlockKey':
      return `/cles-deblocage/${id}`;
    case 'pack':
      return `/ventes/packs/${id}`;
    case 'plan':
      return `/ventes/abonnements/${id}`;
    case 'event':
      return `/evenements/${id}`;
    case 'coach':
      return `/coachs/${id}`;
    case 'module':
      return `/coachs?module=${id}`;
    case 'admin':
      return superAdmin ? `/administrateurs/${id}` : null;
    default:
      return null;
  }
}

const FILTRES = { admin: '', objet: '', action: '', du: '', au: '', echecs: '', cible: '', page: '1' };

export function requeteJournal(f: typeof FILTRES): RequeteJournal {
  return {
    page: Number(f.page) || 1,
    limit: 50,
    ...(f.admin ? { adminId: f.admin } : {}),
    ...(f.objet ? { objectType: f.objet as TypeObjet } : {}),
    ...(f.action ? { action: f.action } : {}),
    ...(f.cible ? { targetId: f.cible } : {}),
    ...(f.du ? { from: bornesJourParis(f.du, 'debut') } : {}),
    ...(f.au ? { to: bornesJourParis(f.au, 'fin') } : {}),
    ...(f.echecs === 'oui' ? { failed: 'true' as const } : {}),
  };
}

/**
 * Journal d'audit (cahier des charges §7.12) : qui a fait quoi, quand, sur
 * quel objet, avec l'état avant et après. Lecture seule, pour tous les admins.
 */
export function JournalPage() {
  const { valeurs: f, modifier, reinitialiser, actifs } = useFiltresUrl(FILTRES);
  const { user } = useSession();
  const toast = useToast();
  const [export_, setExport] = useState(false);

  const choix = useQuery({ queryKey: ['audit', 'choix'], queryFn: () => lire(api.GET('/api/admin/audit/actions')), staleTime: 60_000 });
  const liste = useQuery({
    queryKey: ['audit', 'journal', f],
    queryFn: () => lire(api.GET('/api/admin/audit', { params: { query: requeteJournal(f) } })),
    placeholderData: keepPreviousData,
  });

  /** Actions proposées : celles du type d'objet choisi, groupées par type. */
  const actions = useMemo(() => {
    const groupes = new Map<string, Array<{ action: string; libelle: string; count: number }>>();
    for (const a of choix.data?.actions ?? []) {
      if (a.action.startsWith('GET ')) continue;
      if (f.objet && a.objectType !== f.objet) continue;
      const groupe = a.objectType ? LIBELLE_OBJET[a.objectType] : 'Autres';
      if (!groupes.has(groupe)) groupes.set(groupe, []);
      groupes.get(groupe)!.push({ action: a.action, libelle: libelleAction(a.action), count: a.count });
    }
    return [...groupes.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'fr'))
      .map(([groupe, liste_]) => ({ groupe, actions: liste_.sort((a, b) => a.libelle.localeCompare(b.libelle, 'fr')) }));
  }, [choix.data, f.objet]);

  const exporter = async () => {
    setExport(true);
    try {
      const { page: _p, limit: _l, ...query } = requeteJournal(f);
      enregistrer(await lire(api.GET('/api/admin/audit/export', { params: { query }, parseAs: 'blob' })), nomDate('journal-audit'));
    } catch (err) {
      toast.erreur(messageDe(err));
    } finally {
      setExport(false);
    }
  };

  const colonnes: Colonne<Entree>[] = [
    { id: 'date', header: 'Date', width: 150, cell: (e) => <span className="nowrap">{formatDateTime(e.createdAt)}</span> },
    {
      id: 'admin',
      header: 'Admin',
      cell: (e) => (
        <span className="nowrap">
          <span className="cell-ellipsis">{e.adminEmail ?? 'Inconnu'}</span>
          {e.channel === 'mcp' && (
            <span className="badge-mcp" title="Action faite par un assistant IA connecté au compte (MCP)">
              MCP
            </span>
          )}
        </span>
      ),
    },
    {
      id: 'action',
      header: 'Action',
      cell: (e) => (
        <div>
          <div className="cell-main">
            {libelleAction(e.action)} {e.statusCode >= 400 && <Badge tone="danger">Refusé ({e.statusCode})</Badge>}
          </div>
          {e.reason && <div className="cell-sub">Motif : « {e.reason} »</div>}
        </div>
      ),
    },
    {
      id: 'objet',
      header: 'Objet',
      cell: (e) => {
        const lien = lienObjet(e.objectType, e.targetId, !!user?.isSuperAdmin);
        const libelle = e.targetLabel ?? (e.targetId ? 'Supprimé ou inconnu' : '—');
        return (
          <div>
            {e.objectType && <div className="cell-sub">{LIBELLE_OBJET[e.objectType]}</div>}
            {lien && e.targetLabel ? (
              <Link className="link" to={lien}>
                {libelle}
              </Link>
            ) : (
              <span className={e.targetLabel ? undefined : 'muted'}>{libelle}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'detail',
      header: 'Avant → après',
      secondaire: true,
      cell: (e) => {
        const d = differences(e.before, e.after);
        return d.length ? <div className="journal__diff">{d.join(' · ')}</div> : <span className="muted">—</span>;
      },
    },
  ];

  return (
    <>
      <PageHeader title="Journal d’audit" lead="Chaque action faite depuis le back-office, avec son auteur, son objet et le motif demandé. Rien ne s’efface." />
      <div className="toolbar" role="search">
        <Filtre id="j-admin" label="Admin">
          <Select id="j-admin" value={f.admin} onChange={(e) => modifier({ admin: e.target.value })}>
            <option value="">Tous</option>
            {(choix.data?.admins ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.email ?? a.id}
              </option>
            ))}
          </Select>
        </Filtre>
        <Filtre id="j-objet" label="Objet">
          <Select id="j-objet" value={f.objet} onChange={(e) => modifier({ objet: e.target.value, action: '' })}>
            <option value="">Tous</option>
            {(Object.entries(LIBELLE_OBJET) as Array<[TypeObjet, string]>)
              .sort(([, a], [, b]) => a.localeCompare(b, 'fr'))
              .map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
          </Select>
        </Filtre>
        <Filtre id="j-action" label="Action">
          <Select id="j-action" value={f.action} onChange={(e) => modifier({ action: e.target.value })} className="select--large">
            <option value="">Toutes</option>
            {actions.map((g) => (
              <optgroup key={g.groupe} label={g.groupe}>
                {g.actions.map((a) => (
                  <option key={a.action} value={a.action}>
                    {a.libelle} ({a.count})
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </Filtre>
        <Filtre id="j-du" label="Du">
          <Input id="j-du" type="date" value={f.du} max={f.au || jourParis()} onChange={(e) => modifier({ du: e.target.value })} />
        </Filtre>
        <Filtre id="j-au" label="Au">
          <Input id="j-au" type="date" value={f.au} min={f.du || undefined} max={jourParis()} onChange={(e) => modifier({ au: e.target.value })} />
        </Filtre>
        <Filtre id="j-echecs" label="Résultat">
          <Select id="j-echecs" value={f.echecs} onChange={(e) => modifier({ echecs: e.target.value })}>
            <option value="">Tous</option>
            <option value="oui">Refusés ou en échec</option>
          </Select>
        </Filtre>
        {actifs > 0 && (
          <Button variant="ghost" size="sm" icon={<Filter size={14} aria-hidden="true" />} onClick={reinitialiser}>
            Effacer ({actifs})
          </Button>
        )}
        <span className="toolbar__spacer" />
        <Button icon={<Download size={15} aria-hidden="true" />} onClick={() => void exporter()} loading={export_}>
          Exporter (CSV)
        </Button>
      </div>
      {f.cible && (
        <p className="totaux">
          Actions sur un seul objet.{' '}
          <button type="button" className="lien-discret" onClick={() => modifier({ cible: '' })}>
            Voir tout le journal
          </button>
        </p>
      )}

      {liste.isError ? (
        <ErreurChargement erreur={liste.error} onReessayer={() => void liste.refetch()} />
      ) : (
        <DataTable
          libelle="Journal d’audit"
          colonnes={colonnes}
          lignes={liste.data?.entries}
          cleLigne={(e) => e.id}
          chargement={liste.isFetching && !liste.data}
          pagination={liste.data ? { page: liste.data.pagination.page, pages: liste.data.pagination.pages, total: liste.data.pagination.total } : undefined}
          onPage={(page) => modifier({ page: String(page) })}
          vide={
            <EmptyState
              icon={<ClipboardList size={26} aria-hidden="true" />}
              title={actifs > 0 ? 'Aucune action ne correspond' : 'Aucune action pour l’instant'}
              text={actifs > 0 ? 'Élargissez la période ou retirez un filtre.' : undefined}
            />
          }
        />
      )}
    </>
  );
}
